import { _Componentes_WebApiCalls_Post } from "../ComponentesGlobales/WebApiCalls";

const cachePorConexion = new Map();
const solicitudesPorConexion = new Map();
const versionPorConexion = new Map();

const incrementarVersion = (conexion) => {
  const siguiente = (versionPorConexion.get(conexion) || 0) + 1;
  versionPorConexion.set(conexion, siguiente);
  return siguiente;
};

export const normalizarTextoBusqueda = (valor) =>
  String(valor ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLocaleLowerCase("es")
    .trim();

export const normalizarClienteInformacion = (cliente = {}) => ({
  ...cliente,
  Id: Number(cliente.Id) || 0,
  sCodigo: String(cliente.sCodigo ?? ""),
  sDescripcion: String(cliente.sDescripcion ?? ""),
  sDocumento: String(cliente.sDocumento ?? cliente.Documento ?? ""),
  sCondicionIVA: String(
    cliente.sCondicionIVA ?? cliente.CondicionIVADescripcion ?? ""
  ),
  sTipoDocumento: String(
    cliente.sTipoDocumento ?? cliente.TipoDocumentoDescripcion ?? ""
  ),
});

export const normalizarClienteEditable = (cliente = {}) => ({
  Id: Number(cliente.Id) || 0,
  sCodigo: String(cliente.sCodigo ?? cliente.Codigo ?? ""),
  sDescripcion: String(cliente.sDescripcion ?? cliente.Descripcion ?? ""),
  Documento: String(cliente.Documento ?? cliente.sDocumento ?? ""),
  TipoDocumentoId: Number(cliente.TipoDocumentoId) || 0,
  TipoDocumentoDescripcion: String(
    cliente.TipoDocumentoDescripcion ?? cliente.sTipoDocumento ?? ""
  ),
  CondicionIVAId: Number(cliente.CondicionIVAId) || 0,
  CondicionIVADescripcion: String(
    cliente.CondicionIVADescripcion ?? cliente.sCondicionIVA ?? ""
  ),
});

export const filtrarClientes = (lista = [], consulta = "") => {
  const texto = normalizarTextoBusqueda(consulta);
  if (!texto) return [...lista];
  return lista.filter((cliente) =>
    [cliente?.sCodigo, cliente?.sDescripcion, cliente?.sDocumento]
      .some((valor) => normalizarTextoBusqueda(valor).includes(texto))
  );
};

export const buscarClienteConsumidorFinal = (lista = []) => {
  const clientes = Array.isArray(lista) ? lista : [];
  return clientes.find((cliente) => normalizarTextoBusqueda(cliente?.sCodigo).replace(/[^a-z0-9]/g, "") === "cf")
    || clientes.find((cliente) => normalizarTextoBusqueda(cliente?.sDescripcion).includes("consumidor final"))
    || null;
};

export const formatearDocumentoCliente = (cliente = {}) => {
  const documento = String(cliente.sDocumento ?? "");
  const digitos = documento.replace(/\D/g, "");
  const tipo = normalizarTextoBusqueda(cliente.sTipoDocumento);
  return tipo === "cuit" && digitos.length === 11
    ? `${digitos.slice(0, 2)}-${digitos.slice(2, 10)}-${digitos.slice(10)}`
    : documento;
};

export const detalleCliente = (cliente = {}) => {
  const documento = formatearDocumentoCliente(cliente);
  return [
    [cliente.sTipoDocumento, documento].filter(Boolean).join(" "),
    cliente.sCondicionIVA,
  ].filter(Boolean);
};

export const listarClientes = async ({ EmpresaId, forzar = false } = {}, opciones) => {
  const conexion = Number(EmpresaId);
  if (!conexion) return { error: "No se encontró la conexión a la base de datos." };
  if (!forzar && cachePorConexion.has(conexion)) {
    return { data: { Lista: cachePorConexion.get(conexion), Error: "" }, desdeCache: true };
  }
  if (!forzar && solicitudesPorConexion.has(conexion)) return solicitudesPorConexion.get(conexion);

  const versionSolicitud = versionPorConexion.get(conexion) || 0;
  const solicitud = _Componentes_WebApiCalls_Post(
    "Clientes/Listar",
    { EmpresaId: conexion },
    opciones
  ).then((respuesta) => {
    if (respuesta.error || respuesta.aborted) return respuesta;
    const lista = Array.isArray(respuesta.data?.Lista)
      ? respuesta.data.Lista.map(normalizarClienteInformacion).filter((cliente) => cliente.Id > 0)
      : [];
    if ((versionPorConexion.get(conexion) || 0) === versionSolicitud) {
      cachePorConexion.set(conexion, lista);
    } else if (cachePorConexion.has(conexion)) {
      return {
        ...respuesta,
        data: { ...respuesta.data, Lista: cachePorConexion.get(conexion) },
        obsoleta: true,
      };
    }
    return { ...respuesta, data: { ...respuesta.data, Lista: lista } };
  }).finally(() => {
    if (solicitudesPorConexion.get(conexion) === solicitud) {
      solicitudesPorConexion.delete(conexion);
    }
  });

  solicitudesPorConexion.set(conexion, solicitud);
  return solicitud;
};

export const invalidarCacheClientes = (EmpresaId) => {
  if (EmpresaId === undefined) {
    for (const conexion of new Set([...cachePorConexion.keys(), ...solicitudesPorConexion.keys()])) {
      incrementarVersion(conexion);
    }
    cachePorConexion.clear();
    solicitudesPorConexion.clear();
  } else {
    const conexion = Number(EmpresaId);
    incrementarVersion(conexion);
    cachePorConexion.delete(conexion);
    solicitudesPorConexion.delete(conexion);
  }
};

export const reemplazarClienteEnCache = (EmpresaId, cliente) => {
  const conexion = Number(EmpresaId);
  const actualizado = normalizarClienteInformacion(cliente);
  if (!conexion || actualizado.Id <= 0) return null;

  incrementarVersion(conexion);
  solicitudesPorConexion.delete(conexion);
  const lista = cachePorConexion.get(conexion);
  if (lista) {
    cachePorConexion.set(
      conexion,
      lista.map((item) => Number(item.Id) === actualizado.Id ? actualizado : item)
    );
  }
  return actualizado;
};

export const obtenerCliente = ({ EmpresaId, ClienteId }, opciones) =>
  _Componentes_WebApiCalls_Post("Clientes/Obtener", {
    EmpresaId: Number(EmpresaId),
    ClienteId: Number(ClienteId) || 0,
  }, opciones);

export const actualizarCliente = ({
  EmpresaId,
  Cliente,
  OperadorId = 0,
  PcId = 0,
}, opciones) => {
  const editable = normalizarClienteEditable(Cliente);
  return _Componentes_WebApiCalls_Post("Clientes/Actualizar", {
    EmpresaId: Number(EmpresaId),
    Cliente: {
      Id: editable.Id,
      sCodigo: editable.sCodigo,
      sDescripcion: editable.sDescripcion,
      Documento: editable.Documento,
      TipoDocumentoId: editable.TipoDocumentoId,
      CondicionIVAId: editable.CondicionIVAId,
    },
    OperadorId: Number(OperadorId) || 0,
    PcId: Number(PcId) || 0,
  }, opciones);
};

export const eliminarCliente = ({
  EmpresaId,
  ClienteId,
  OperadorId = 0,
  PcId = 0,
}, opciones) =>
  _Componentes_WebApiCalls_Post("Clientes/Eliminar", {
    EmpresaId: Number(EmpresaId),
    ClienteId: Number(ClienteId) || 0,
    OperadorId: Number(OperadorId) || 0,
    PcId: Number(PcId) || 0,
  }, opciones);

export const limpiarCacheClientesParaPruebas = () => {
  cachePorConexion.clear();
  solicitudesPorConexion.clear();
  versionPorConexion.clear();
};
